import * as JsDiff from "diff";

export enum DiffMode {
  Line = "Line",
  Sentence = "Sentence",
  Word = "Word",
  WordWithSpaces = "WordWithSpaces",
}

/** Matches a line that begins with a list marker or heading marker,
 *  where an ins/del tag has been placed BEFORE the marker. Capture groups:
 *  leading whitespace, tag, post-tag whitespace, marker (incl. trailing space). */
const markerAfterTagRegex = /^([ \t]*)<(ins|del)>([ \t]*)((?:\d+\.|[-*+]|#{1,6})[ \t]+)/gm;

/** Empty tag pairs left behind by splitting changes at newlines. */
const emptyTagPairRegex = /<(ins|del)><\/\1>/g;

/**
 * Produces markdown with <ins>/<del> tags marking the differences between two
 * markdown sources. Used as the LEGACY display path (items without authored
 * markedUpText, i.e. content from past years) and to generate first-draft
 * mark-up for organisers to correct in the editor.
 *
 * Improvements over the original implementation:
 *  - whitespace-only changes are not tagged (they rendered as empty highlights)
 *  - empty <ins></ins>/<del></del> pairs are removed
 *  - tags never swallow list/heading markers at the start of a line, so list
 *    structure and numbering survive the diff (previously `<ins>3. ` stopped
 *    markdown from recognising the item as part of the list)
 */
export function generateDiffMarkup(oldString: string, newString: string, diffMode: string = DiffMode.Word): string {
  let parts: JsDiff.Change[];
  switch (diffMode) {
    case DiffMode.Sentence:
      parts = JsDiff.diffSentences(oldString, newString);
      break;
    case DiffMode.Line:
      parts = JsDiff.diffLines(oldString, newString);
      break;
    case DiffMode.WordWithSpaces:
      parts = JsDiff.diffWordsWithSpace(oldString, newString);
      break;
    default:
      parts = JsDiff.diffWords(oldString, newString);
      break;
  }

  const output: string[] = [];
  for (const part of parts) {
    const value = part.value;
    if (!part.added && !part.removed) {
      output.push(value);
      continue;
    }
    // a pure-whitespace change carries no readable information; keep the
    // whitespace but don't highlight it
    if (value.trim() === "") {
      output.push(value);
      continue;
    }
    const prefix = part.added ? "<ins>" : "<del>";
    const posfix = part.added ? "</ins>" : "</del>";
    // inline ins/del tags cannot legally span line/block boundaries, so close
    // and reopen the tag around every newline run
    output.push(`${prefix}${value}${posfix}`.replace(/([\r\n]+)/gm, `${posfix}$1${prefix}`));
  }

  return output
    .join("")
    .replace(emptyTagPairRegex, "")
    .replace(markerAfterTagRegex, "$1$3$4<$2>")
    .replace(emptyTagPairRegex, "");
}

/** Retained for compatibility with existing imports. */
export class Generator {
  public exec(oldString: string, newString: string, diffMode: string) {
    return generateDiffMarkup(oldString, newString, diffMode);
  }
}
